/**
 * Cloudflare Turnstile's widget, for the screens self-serve signup puts in
 * front of strangers: the sign-in form's link to a new address, and making an
 * organisation (workers/api/src/lib/turnstile.ts checks the token).
 *
 * Rendered explicitly rather than by the script finding a `cf-turnstile` div,
 * because this is a single-page app: the widget comes and goes with the
 * screen, and a token is spent by one request, so the screen resets the
 * widget after every attempt (`reset()` on the handle) for a fresh one.
 *
 * The script is loaded once, on first use, from challenges.cloudflare.com,
 * which `_headers`' CSP lets in for scripts and frames and nothing else.
 */

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

interface TurnstileApi {
  render(element: HTMLElement, options: Record<string, unknown>): string;
  reset(widget: string): void;
  remove(widget: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let loading: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  loading ??= new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT;
    script.async = true;
    script.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error("no turnstile")));
    script.onerror = () => {
      // A later screen may try again: a dropped connection is not for ever.
      loading = null;
      script.remove();
      reject(new Error("turnstile did not load"));
    };
    document.head.appendChild(script);
  });
  return loading;
}

export interface TurnstileHandle {
  /** A fresh challenge, after the token was spent or refused. */
  reset(): void;
}

/** The actions the worker checks for, one per screen. */
export type TurnstileAction = "signin" | "signup";

export const Turnstile = forwardRef<
  TurnstileHandle,
  { siteKey: string; action: TurnstileAction; onToken: (token: string | null) => void }
>(function Turnstile({ siteKey, action, onToken }, handle) {
  const box = useRef<HTMLDivElement>(null);
  const widget = useRef<string | null>(null);
  const told = useRef(onToken);
  told.current = onToken;
  const [failed, setFailed] = useState(false);

  useImperativeHandle(handle, () => ({
    reset() {
      told.current(null);
      if (widget.current && window.turnstile) window.turnstile.reset(widget.current);
    },
  }));

  useEffect(() => {
    let live = true;
    loadTurnstile()
      .then((turnstile) => {
        if (!live || !box.current) return;
        widget.current = turnstile.render(box.current, {
          sitekey: siteKey,
          action,
          theme: "auto",
          size: "flexible",
          callback: (token: string) => told.current(token),
          "expired-callback": () => told.current(null),
          "error-callback": () => told.current(null),
        });
      })
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
      if (widget.current && window.turnstile) window.turnstile.remove(widget.current);
      widget.current = null;
    };
  }, [siteKey, action]);

  return (
    <div className="turnstile">
      <div ref={box} />
      {failed ? (
        <p className="problem" role="alert">
          The check on this page could not load. Check your connection and reload.
        </p>
      ) : null}
    </div>
  );
});
